import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Handshake, Eye, EyeOff, Lock, Mail, Loader2 } from "lucide-react";

export default function PartnerLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const API = `${window.location.origin}/api`;

  useEffect(() => {
    // إذا الشريك مسجّل دخول مسبقاً، انقله للـ Dashboard
    if (localStorage.getItem("partner_token")) {
      navigate("/partner-dashboard");
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("املأ البريد وكلمة المرور");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/partner/login`, { email: email.trim().toLowerCase(), password });
      localStorage.setItem("partner_token", res.data.token);
      localStorage.setItem("partner_info", JSON.stringify(res.data.partner));
      toast.success(`أهلاً ${res.data.partner.name}`);
      navigate("/partner-dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل الدخول");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 p-4" dir="rtl" data-testid="partner-login-page">
      <Card className="w-full max-w-md bg-slate-900/80 border border-emerald-500/30 backdrop-blur shadow-2xl">
        <CardContent className="p-8 space-y-6">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
              <Handshake className="w-11 h-11 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-1">لوحة الشريك</h1>
            <p className="text-slate-400 text-sm">دخول مخصص للشركاء — عرض شفاف للأرقام</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-300 mb-1.5 block flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> البريد الإلكتروني
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="partner@example.com"
                className="bg-slate-800 border-slate-700 text-white"
                required
                autoFocus
                data-testid="partner-email-input"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-1.5 block flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" /> كلمة المرور
              </Label>
              <div className="relative">
                <Input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-slate-800 border-slate-700 text-white pe-10"
                  required
                  data-testid="partner-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute end-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white py-6 text-base font-bold"
              data-testid="partner-login-submit-btn"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 ml-2 animate-spin" /> جاري الدخول...</>
              ) : (
                "دخول"
              )}
            </Button>
          </form>

          <div className="pt-4 border-t border-slate-700 text-center">
            <p className="text-xs text-slate-500">
              هذه صفحة مخصصة للشركاء فقط. للمستخدمين العاديين:{" "}
              <button onClick={() => navigate("/login")} className="text-emerald-400 hover:underline">
                تسجيل الدخول العادي
              </button>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
